import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RefreshCw, Wifi, WifiOff, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ProxyStatus = ({ state, onCheck, checking }) => {
  const proxies = Object.entries(state?.proxies || {});
  const ok = state?.all_ok !== false;
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="ott-proxy-status">
      {ok ? <Wifi className="h-5 w-5 text-emerald-600" /> : <WifiOff className="h-5 w-5 text-red-600" />}
      <div>
        <div className={`text-sm font-semibold ${ok ? 'text-emerald-700' : 'text-red-700'}`} data-testid="ott-proxy-status-label">
          {state ? (ok ? 'OTT proxy reachable' : 'OTT proxy DOWN — activations will fail') : 'OTT proxy not checked yet'}
        </div>
        {state?.server_egress_ip && (
          <div className="text-xs text-slate-700" data-testid="ott-server-egress-ip">
            Server outgoing IP (whitelist this on the proxy): <span className="font-mono font-semibold">{state.server_egress_ip}</span>
          </div>
        )}
        {proxies.map(([url, p]) => (
          <div key={url} className="text-xs text-slate-500">
            {url.split('://').pop()} ({p.used_by.join(', ')}) · {p.ok ? `egress ${p.egress_ip}` : p.error} · checked {new Date(p.checked_at).toLocaleTimeString()}
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" onClick={onCheck} disabled={checking} data-testid="ott-proxy-check-now-btn">
        <RefreshCw className={`h-3.5 w-3.5 mr-1 ${checking ? 'animate-spin' : ''}`} />Check now
      </Button>
    </div>
  );
};

export const OttProxyHealthBar = ({ token, onRetried }) => {
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/ott/proxy-health`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return null;
    const d = await r.json();
    setHealth(d);
    return d;
  }, [token]);

  const checkNow = async () => {
    setChecking(true);
    try {
      const r = await fetch(`${API}/api/ott/proxy-health/check-now`, { method: 'POST', headers: auth });
      if (!r.ok) toast.error('Proxy check failed');
      await load();
    } catch { toast.error('Proxy check failed'); }
    finally { setChecking(false); }
  };

  const poll = (jobId) => {
    timer.current = setInterval(async () => {
      const r = await fetch(`${API}/api/ott/activations/bulk-retry/${jobId}`, { headers: auth });
      if (!r.ok) return;
      const j = await r.json();
      setJob(j);
      if (j.status === 'completed') {
        clearInterval(timer.current);
        toast.success(`Bulk retry done: ${j.succeeded} activated, ${j.failed} failed`);
        load(); onRetried();
      }
    }, 2000);
  };

  useEffect(() => {
    load().then(d => { if (d?.running_job) { setJob(d.running_job); poll(d.running_job.job_id); } });
    return () => clearInterval(timer.current);
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const bulkRetry = async () => {
    if (!window.confirm(`Retry all ${health?.pending_count} pending activation(s) now?`)) return;
    try {
      const r = await fetch(`${API}/api/ott/activations/bulk-retry-pending`, { method: 'POST', headers: auth });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return toast.error(d.detail || 'Bulk retry failed to start');
      setJob(d); poll(d.job_id);
    } catch { toast.error('Bulk retry failed to start'); }
  };

  const running = job?.status === 'running';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4 space-y-3" data-testid="ott-proxy-health-bar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ProxyStatus state={health?.state} onCheck={checkNow} checking={checking} />
        <Button onClick={bulkRetry} disabled={running || !health?.pending_count} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="ott-bulk-retry-pending-btn">
          <RotateCcw className={`h-4 w-4 mr-2 ${running ? 'animate-spin' : ''}`} />
          Bulk Retry Pending <span className="ml-1.5 rounded-full bg-white/20 px-2 text-xs" data-testid="ott-pending-count">{health?.pending_count ?? 0}</span>
        </Button>
      </div>
      {job && (
        <div className="space-y-1" data-testid="ott-bulk-retry-progress">
          <Progress value={(job.done / job.total) * 100} className="h-2" />
          <div className="text-xs text-slate-600">
            {running ? 'Retrying' : 'Finished'} {job.done}/{job.total} · <span className="text-emerald-700">{job.succeeded} activated</span> · <span className="text-red-700">{job.failed} failed</span>
          </div>
        </div>
      )}
    </div>
  );
};
